// src/hooks/useReportApi.tsx

import { useState, useCallback } from 'react'

import { FolderId } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api/'

import { refreshToken } from './auth/'

const {
  REACT_APP_API_URL,
  REACT_APP_REPORTS_API_PREFIX,
  REACT_APP_REPORTS_ENV,
} = import.meta.env

const ERROR_CODES = new Set([
  'ERR_NO_FOLDERID',
  'ERR_NO_YEAR',
  'ERR_NO_MONTH',
  'ERR_NO_QUERY_STRING_PARAMETERS',
  'ERR_GETTING_FOLDERDATA',
  'ERR_GETTING_PRICEDATA',
  'ERR_SAVING_PDF',
])

export const useReportApi = () => {
  const [isLoading, setLoading] = useState(false)
  const [isError, setError] = useState(null)
  const [status, setStatus] = useState('bereit')
  const [report, setReport] = useState(null)

  const generatePdf = useCallback(async (id: FolderId, month, year, list) => {
    setLoading(true)
    setError(null)
    setStatus('Bericht wird generiert...')
    try {
      await refreshToken()
      const url = `https://${REACT_APP_REPORTS_API_PREFIX}.execute-api.eu-central-1.amazonaws.com/${REACT_APP_REPORTS_ENV}/reports/get?folder_id=${id}&year=${year}&month=${month}&sensors=${list}`
      const options = {
        method: 'POST',
        headers: { ...authHeader() },
        body: '',
      }
      const response = await fetch(url, options)
      const text = await response.text()
      const json = text ? JSON.parse(text) : {}
      setReport(json)
      const msg = json?.message
      if (ERROR_CODES.has(msg)) {
        setError(msg)
        setStatus('')
        notifyError(msg)
      } else {
        setStatus(msg || 'fertig')
      }
      return json
    } catch (error) {
      const message = error?.message
      setError(message)
      setStatus('')
      notifyError(message)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const createSettings = useCallback(
    async (id: FolderId, recipients, sensorIds) => {
      setLoading(true)
      setError(null)
      setStatus('Dauerauftrag erstellt...')
      try {
        await refreshToken()
        const url = `${REACT_APP_API_URL}/user/report-settings`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            folder_id: id,
            mailto: recipients,
            sensor_ids: sensorIds,
          }),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }
        const json = await response.json()
        setReport(json)
        setStatus(json?.message || 'fertig')
        notifySuccess('Report abonniert')
        return json
      } catch (error) {
        const message = error?.message || 'Fehler beim Erstellen'
        setError(message)
        setStatus('')
        notifyError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteSettings = useCallback(async (id: FolderId) => {
    setLoading(true)
    setError(null)
    setStatus('Dauerauftrag gelöscht...')
    try {
      await refreshToken()
      const url = `${REACT_APP_API_URL}/user/report-settings`
      const options = {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        method: 'DELETE',
        body: JSON.stringify({ folder_id: id }),
      }
      const response = await fetch(url, options)
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `HTTP ${response.status}`)
      }
      const json = await response.json()
      setReport(json)
      setStatus(json?.message || 'fertig')
      notifySuccess('Abonnement gelöscht')
      return json
    } catch (error) {
      const message = error?.message || 'Fehler beim Löschen'
      setError(message)
      setStatus('')
      notifyError(message)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const updateSettings = useCallback(
    async (id: FolderId, recipients, sensorIds) => {
      setLoading(true)
      setError(null)
      setStatus('Dauerauftrag aktualisiert...')
      try {
        await refreshToken()
        const url = `${REACT_APP_API_URL}/user/report-settings`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            folder_id: id,
            mailto: recipients,
            sensor_ids: sensorIds,
          }),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }
        const json = await response.json()
        setReport(json)
        setStatus(json?.message || 'fertig')
        notifySuccess('Abonnement aktualisiert')
        return json
      } catch (error) {
        const message = error?.message || 'Fehler beim Aktualisieren'
        setError(message)
        setStatus('')
        notifyError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return {
    generatePdf,
    createSettings,
    deleteSettings,
    updateSettings,
    isLoading,
    isError,
    status,
    report,
  }
}
